import { client } from '@/lib/api/client'
import {
  venueCampaignEstimatePath,
  venueCampaignPath,
  venueCampaignRecipientsPath,
  venueCampaignResultPath,
  venueCampaignSchedulePath,
  venueCampaignSendPath,
  venueCampaignsPath,
  venueCampaignSummaryPath,
} from '@/lib/api/config'
import { toApiError } from '@/lib/api/errors'

import type {
  VenueCampaignCreateInput,
  VenueCampaignDetailView,
  VenueCampaignEstimateView,
  VenueCampaignHistorySummaryView,
  VenueCampaignListView,
  VenueCampaignRecipientsView,
  VenueCampaignResultView,
  VenueCampaignSendResult,
  VenueCampaignUpdateInput,
} from './campaignTypes'
import type { CampaignEstimateParams, CampaignListFilter, RecipientReadFilter } from './campaignViewModel'
import { campaignListQueryParams, recipientReadParam } from './campaignViewModel'

/** `GET campaigns/summary` (#728 §3). 게이팅 없음(`ALWAYS_OPEN`) — 이용권이 만료돼도 항상 200. */
export async function fetchCampaignSummary(
  venueId: string,
  params: { from: string; to: string; type?: 'NOTICE' | 'PROMOTION' }
): Promise<VenueCampaignHistorySummaryView> {
  try {
    const { data } = await client.get<VenueCampaignHistorySummaryView>(venueCampaignSummaryPath(venueId), {
      params,
    })
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/** `GET campaigns` 목록(#714 §1-5, #728 §4 확장) — 커서 페이지네이션. 게이팅 없음. */
export async function fetchCampaigns(
  venueId: string,
  filter: CampaignListFilter,
  cursor?: string
): Promise<VenueCampaignListView> {
  try {
    const { data } = await client.get<VenueCampaignListView>(venueCampaignsPath(venueId), {
      params: campaignListQueryParams(filter, cursor),
    })
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/** `GET campaigns/{id}` 상세(#714 §1-6). 게이팅 없음. 타 구장 id는 404(존재 은닉). */
export async function fetchCampaignDetail(venueId: string, campaignId: number): Promise<VenueCampaignDetailView> {
  try {
    const { data } = await client.get<VenueCampaignDetailView>(venueCampaignPath(venueId, campaignId))
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/** `GET campaigns/estimate` (#714 §1-7). 작성과 같은 게이트 — 이용권 만료 시 409(`40913`). */
export async function fetchCampaignEstimate(
  venueId: string,
  params: CampaignEstimateParams
): Promise<VenueCampaignEstimateView> {
  try {
    const { data } = await client.get<VenueCampaignEstimateView>(venueCampaignEstimatePath(venueId), {
      params,
    })
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/** `POST campaigns` — 작성(#714 §1-1). 이 화면은 `type:'NOTICE'`·`scheduledAt:null` 고정 입력만 보낸다. */
export async function createCampaign(
  venueId: string,
  input: VenueCampaignCreateInput
): Promise<VenueCampaignDetailView> {
  try {
    const { data } = await client.post<VenueCampaignDetailView>(venueCampaignsPath(venueId), input)
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `PUT campaigns/{id}` — 내용 수정(#714 §4-1, #732에서 처음 소비). DRAFT 전용으로 호출부가 제한한다
 * (§자율 결정 6) — `SENT`·`SENDING`·`FAILED`에 호출하면 409(`40938`).
 */
export async function updateCampaign(
  venueId: string,
  campaignId: number,
  input: VenueCampaignUpdateInput
): Promise<VenueCampaignDetailView> {
  try {
    const { data } = await client.put<VenueCampaignDetailView>(venueCampaignPath(venueId, campaignId), input)
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/** `PUT campaigns/{id}/schedule` — DRAFT→SCHEDULED 확정(#714 §4-2, #732에서 처음 소비). */
export async function scheduleCampaign(
  venueId: string,
  campaignId: number,
  scheduledAt: string
): Promise<VenueCampaignDetailView> {
  try {
    const { data } = await client.put<VenueCampaignDetailView>(venueCampaignSchedulePath(venueId, campaignId), {
      scheduledAt,
    })
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `POST campaigns/{id}/send` (#715 §D-1).
 * 🚨 `202`(통과)·`200`(차단) 둘 다 HTTP 성공이다 — `dispatched` 필드로 성공/차단을 가른다.
 */
export async function sendCampaign(venueId: string, campaignId: number): Promise<VenueCampaignSendResult> {
  try {
    const { data } = await client.post<VenueCampaignSendResult>(venueCampaignSendPath(venueId, campaignId))
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/** `GET campaigns/{id}/result` (#715 §D-2, #728 §5). 게이팅 없음 — 과금된 발송 결과는 항상 조회 가능. */
export async function fetchCampaignResult(venueId: string, campaignId: number): Promise<VenueCampaignResultView> {
  try {
    const { data } = await client.get<VenueCampaignResultView>(venueCampaignResultPath(venueId, campaignId))
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/** `GET campaigns/{id}/recipients` (#728 §6) — 커서 페이지네이션. 게이팅 없음. */
export async function fetchCampaignRecipients(
  venueId: string,
  campaignId: number,
  filter: { read: RecipientReadFilter },
  cursor?: string
): Promise<VenueCampaignRecipientsView> {
  try {
    const { data } = await client.get<VenueCampaignRecipientsView>(
      venueCampaignRecipientsPath(venueId, campaignId),
      { params: { read: recipientReadParam(filter.read), cursor } }
    )
    return data
  } catch (error) {
    throw toApiError(error)
  }
}
